import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Modal } from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/Button";
import { Input } from "../../../components/ui/Input";
import { cn } from "../../../utils/cn";
import { RUT_STATE, rutErrorKey, validateRut } from "../../../utils/rut";
import { useCountry } from "../../../context/CountryContext";
import { PHONE_PREFIX, STAGE_ORDER, emptyForm } from "./prospectsShared";

// Campo del formulario: label + input/textarea/select + error.
function Field({ label, htmlFor, error, children, className }) {
  return (
    <div className={cn("space-y-1", className)}>
      <label
        htmlFor={htmlFor}
        className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider"
      >
        {label}
      </label>
      {children}
      {error && <span className="block text-xs text-status-error">{error}</span>}
    </div>
  );
}

export default function ProspectFormModal({
  open,
  editing,
  submitting,
  onClose,
  onSubmit,
}) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: emptyForm, mode: "onSubmit" });

  const [rutTouched, setRutTouched] = useState(false);

  // Resetea el formulario cada vez que se abre (create vs edit).
  React.useEffect(() => {
    if (!open) return;
    setRutTouched(false);
    reset(
      editing
        ? {
            fullName: editing.fullName || "",
            companyName: editing.companyName || "",
            phone: editing.phone || PHONE_PREFIX,
            email: editing.email || "",
            industry: editing.industry || "",
            website: editing.website || "",
            notes: editing.notes || "",
            businessIdentifier: editing.businessIdentifier || "",
            stage: STAGE_ORDER.includes(editing.stage) ? editing.stage : "nuevo",
            blocker: editing.blocker || "",
          }
        : emptyForm,
    );
  }, [open, editing, reset]);

  // El RUT se revalida en vivo para avisar al tiro del DV equivocado; el bloqueo del
  // guardado vive en el validador de register (ver más abajo).
  const RUT_ERROR_ID = "prospect-rut-error";
  // Fuera de Chile el identificador tributario del prospecto es un CUIT/NIT: el módulo 11
  // chileno solo aplica con el país de operación en CL.
  const { countryCode } = useCountry();
  const esChile = countryCode === "CL";
  const rutCheck = validateRut(watch("businessIdentifier"));
  // "Incompleto" solo después de salir del campo, para no marcar en rojo a quien todavía
  // está escribiendo; el DV equivocado es definitivo y se avisa de inmediato.
  const rutError =
    esChile &&
    (rutCheck.state === RUT_STATE.BAD_DV ||
      (rutTouched && rutCheck.state === RUT_STATE.INCOMPLETE))
      ? rutErrorKey(rutCheck)
      : null;
  // Si el envío fue rechazado por el validador del register, se muestra ese mismo mensaje.
  const rutMessage = rutError ?? errors.businessIdentifier?.message ?? null;

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    if (!value.startsWith(PHONE_PREFIX.trim())) {
      setValue("phone", PHONE_PREFIX, { shouldValidate: false });
    } else {
      setValue("phone", value, { shouldValidate: false });
    }
  };

  const inputCls =
    "h-10 w-full rounded-lg border bg-bg px-3 text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent";

  return (
    <Modal
      open={open}
      onClose={() => !submitting && onClose()}
      size="lg"
      title={editing ? t("Editar prospecto") : t("Nuevo prospecto")}
      description={t(
        "Datos del comercio y etapa actual del onboarding.",
      )}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {t("Cancelar")}
          </Button>
          <Button
            type="submit"
            form="prospect-form"
            loading={submitting}
          >
            {editing ? t("Guardar cambios") : t("Crear prospecto")}
          </Button>
        </>
      }
    >
      <form
        id="prospect-form"
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-1 sm:grid-cols-2 gap-4"
      >
        <Field
          label={t("Nombre y apellido *")}
          htmlFor="prospect-fullName"
          error={errors.fullName?.message}
        >
          <Input
            id="prospect-fullName"
            autoFocus
            invalid={!!errors.fullName}
            placeholder={t("Ej: Juan Pérez")}
            {...register("fullName", {
              required: t("El nombre es obligatorio."),
              maxLength: { value: 120, message: t("Máximo 120 caracteres.") },
            })}
          />
        </Field>

        <Field
          label={t("Empresa *")}
          htmlFor="prospect-companyName"
          error={errors.companyName?.message}
        >
          <Input
            id="prospect-companyName"
            invalid={!!errors.companyName}
            placeholder={t("Ej: Acme S.A.")}
            {...register("companyName", {
              required: t("La empresa es obligatoria."),
              maxLength: { value: 120, message: t("Máximo 120 caracteres.") },
            })}
          />
        </Field>

        <Field
          label={t("RUT")}
          htmlFor="prospect-businessIdentifier"
        >
          <Input
            id="prospect-businessIdentifier"
            invalid={!!rutMessage}
            aria-invalid={!!rutMessage}
            aria-describedby={rutMessage ? RUT_ERROR_ID : undefined}
            placeholder="76.123.456-0"
            {...register("businessIdentifier", {
              maxLength: { value: 20, message: t("Máximo 20 caracteres.") },
              onBlur: () => setRutTouched(true),
              // Un RUT con el DV equivocado rompe el control de duplicados del backend
              // (la llave es RUT + folio): se bloquea el guardado. Vacío es válido, el
              // formulario no lo exigía antes.
              validate: (value) => {
                if (!esChile) return true;
                const r = validateRut(value);
                if (r.ok || r.state === RUT_STATE.EMPTY) return true;
                return t(rutErrorKey(r), { dv: r.expectedDv });
              },
            })}
          />
          {rutMessage && (
            <p id={RUT_ERROR_ID} className="text-xs text-status-error mt-1" role="alert">
              {rutMessage}
            </p>
          )}
        </Field>

        <Field
          label={t("Teléfono")}
          htmlFor="prospect-phone"
          error={errors.phone?.message}
        >
          <Input
            id="prospect-phone"
            type="tel"
            invalid={!!errors.phone}
            placeholder="+56 9 1234 5678"
            {...register("phone", {
              maxLength: { value: 30, message: t("Máximo 30 caracteres.") },
            })}
            onChange={handlePhoneChange}
          />
        </Field>

        <Field
          label={t("Email")}
          htmlFor="prospect-email"
          error={errors.email?.message}
        >
          <Input
            id="prospect-email"
            type="email"
            invalid={!!errors.email}
            placeholder="contacto@empresa.cl"
            {...register("email", {
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t("Email inválido."),
              },
              maxLength: { value: 120, message: t("Máximo 120 caracteres.") },
            })}
          />
        </Field>

        <Field
          label={t("Rubro")}
          htmlFor="prospect-industry"
          error={errors.industry?.message}
        >
          <Input
            id="prospect-industry"
            invalid={!!errors.industry}
            placeholder={t("Ej: Retail, alimentos, e-commerce")}
            {...register("industry", {
              maxLength: { value: 120, message: t("Máximo 120 caracteres.") },
            })}
          />
        </Field>

        <Field label={t("Etapa")} htmlFor="prospect-stage">
          <div className="relative">
            <select
              id="prospect-stage"
              className={cn(inputCls, "appearance-none border-border pr-9")}
              {...register("stage")}
            >
              <option value="nuevo">{t("Nuevo")}</option>
              <option value="contactado">{t("Contactado")}</option>
              <option value="validacion">{t("En validación")}</option>
              <option value="aprobado">{t("Aprobado")}</option>
              <option value="rechazado">{t("Rechazado")}</option>
            </select>
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </div>
        </Field>

        <Field
          label={t("Bloqueante")}
          htmlFor="prospect-blocker"
          error={errors.blocker?.message}
        >
          <Input
            id="prospect-blocker"
            invalid={!!errors.blocker}
            placeholder={t("Ej: Falta firma legal")}
            {...register("blocker", {
              maxLength: { value: 200, message: t("Máximo 200 caracteres.") },
            })}
          />
        </Field>

        <Field
          label={t("Web o Instagram")}
          htmlFor="prospect-website"
          error={errors.website?.message}
          className="sm:col-span-2"
        >
          <Input
            id="prospect-website"
            invalid={!!errors.website}
            placeholder={t("empresa.cl o @empresa")}
            {...register("website", {
              maxLength: { value: 200, message: t("Máximo 200 caracteres.") },
            })}
          />
        </Field>

        <Field
          label={t("Notas adicionales")}
          htmlFor="prospect-notes"
          error={errors.notes?.message}
          className="sm:col-span-2"
        >
          <textarea
            id="prospect-notes"
            rows={3}
            placeholder={t("Contexto, próximos pasos, referidos, etc.")}
            className={cn(
              "w-full rounded-lg border bg-bg px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-y min-h-[72px]",
              errors.notes ? "border-status-error" : "border-border",
            )}
            {...register("notes", {
              maxLength: { value: 2000, message: t("Máximo 2000 caracteres.") },
            })}
          />
        </Field>
      </form>
    </Modal>
  );
}
